import { useState, useEffect } from "react";
import useReveal from "@/hooks/useReveal";

interface Post {
  title: string;
  tag: string;
  date: string;
  img: string;
  href: string;
}

const FALLBACK_TISTORY: Post = {
  title: "인테리어 공사 중 소음 갈등 예방 및 해결 방법｜공사 전 꼭 알아야 할 분쟁 대처법",
  tag: "기술자료 · 티스토리",
  date: "2026.07.18",
  img: "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/cea46f6f-ee04-4f52-9edd-e43f03143240_compressed_unnamed.webp",
  href: "https://jinc1.com",
};

const FALLBACK_BLOGGER: Post = {
  title: "공장 지붕을 다시 칠해야 한다면 일반 도료보다 에너지세이빙 도료가 더 유리할까요?",
  tag: "심층자료 · 구글블로그",
  date: "2026.07.02",
  img: "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/038c6b34-83fa-4505-981c-3a22affcc9bf_compressed_unnamed.webp",
  href: "https://blog.jinc1.com",
};

const FALLBACK_NAVER: Post = {
  title: "에폭시슬라이스공법(에폭시라이닝 500um+에폭시코팅50um) 부산 기장 장안공단 세지솔로텍 천막동 바닥공사",
  tag: "시공사례 · 네이버",
  date: "2026.06.24",
  img: "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/6ff81c25-2295-4129-9d43-fab927ecc86e_compressed_KakaoTalk_20250806_161741950_05.webp",
  href: "https://blog.naver.com/ksh1009000",
};

const FEEDS = [
  {
    rssUrl: "https://api.rss2json.com/v1/api.json?rss_url=https://jinc1.com/rss",
    tag: "기술자료 · 티스토리",
    fallback: FALLBACK_TISTORY,
  },
  {
    rssUrl: "https://api.rss2json.com/v1/api.json?rss_url=https://blog.jinc1.com/feeds/posts/default?alt=rss",
    tag: "심층자료 · 구글블로그",
    fallback: FALLBACK_BLOGGER,
  },
  {
    rssUrl: "https://api.rss2json.com/v1/api.json?rss_url=https://rss.blog.naver.com/ksh1009000.xml",
    tag: "시공사례 · 네이버",
    fallback: FALLBACK_NAVER,
  },
];

function isValidImageUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (trimmed === "") return false;
  if (!/^https?:\/\//i.test(trimmed)) return false;
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractImagesFromHtml(html: string): string[] {
  const results: string[] = [];
  const regex = /<img[^>]+src=["']([^"']+)["']/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    results.push(match[1]);
  }
  return results;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractThumbnail(item: any, fallbackImg: string): string {
  if (item.thumbnail && typeof item.thumbnail === "string" && isValidImageUrl(item.thumbnail)) {
    return item.thumbnail.trim();
  }

  if (item.enclosure && typeof item.enclosure === "object") {
    const enc = item.enclosure;
    if (enc.link && typeof enc.link === "string" && isValidImageUrl(enc.link)) return enc.link.trim();
    if (enc.url && typeof enc.url === "string" && isValidImageUrl(enc.url)) return enc.url.trim();
  }

  const mediaContent = item["media:content"];
  if (mediaContent) {
    if (typeof mediaContent === "string" && isValidImageUrl(mediaContent)) return mediaContent.trim();
    if (typeof mediaContent === "object" && mediaContent.url && isValidImageUrl(mediaContent.url)) return mediaContent.url.trim();
    if (Array.isArray(mediaContent)) {
      for (const mc of mediaContent) {
        if (mc && typeof mc === "object" && mc.url && isValidImageUrl(mc.url)) return mc.url.trim();
        if (mc && typeof mc === "string" && isValidImageUrl(mc)) return mc.trim();
      }
    }
  }

  const mediaThumbnail = item["media:thumbnail"];
  if (mediaThumbnail) {
    if (typeof mediaThumbnail === "string" && isValidImageUrl(mediaThumbnail)) return mediaThumbnail.trim();
    if (typeof mediaThumbnail === "object" && mediaThumbnail.url && isValidImageUrl(mediaThumbnail.url)) return mediaThumbnail.url.trim();
    if (Array.isArray(mediaThumbnail)) {
      for (const mt of mediaThumbnail) {
        if (mt && typeof mt === "object" && mt.url && isValidImageUrl(mt.url)) return mt.url.trim();
        if (mt && typeof mt === "string" && isValidImageUrl(mt)) return mt.trim();
      }
    }
  }

  const mediaGroup = item["media:group"] || item.media;
  if (mediaGroup && typeof mediaGroup === "object") {
    const thumbCandidates = [
      mediaGroup["media:thumbnail"],
      mediaGroup.thumbnail,
      mediaGroup["media:content"],
      mediaGroup.content,
    ];
    for (const candidate of thumbCandidates) {
      if (candidate) {
        if (typeof candidate === "string" && isValidImageUrl(candidate)) return candidate.trim();
        if (typeof candidate === "object" && candidate.url && isValidImageUrl(candidate.url)) return candidate.url.trim();
      }
    }
  }

  const desc = typeof item.description === "string" ? item.description : "";
  const descImgs = extractImagesFromHtml(desc);
  for (const img of descImgs) {
    if (isValidImageUrl(img)) return img;
  }

  const contentHtml = typeof item.content === "string"
    ? item.content
    : (typeof item["content:encoded"] === "string" ? item["content:encoded"] : "");
  const contentImgs = extractImagesFromHtml(contentHtml);
  for (const img of contentImgs) {
    if (isValidImageUrl(img)) return img;
  }

  return fallbackImg;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  } catch {
    return dateStr;
  }
}

function formatTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${y}.${m}.${day} ${h}:${min}`;
  } catch {
    return dateStr;
  }
}

function decodeHtmlEntities(text: string): string {
  if (!text) return text;
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

async function fetchPostsFromFeed(feed: (typeof FEEDS)[number], count: number = 5): Promise<Post[]> {
  try {
    const res = await fetch(feed.rssUrl);
    if (!res.ok) throw new Error("RSS fetch failed");
    const data = await res.json();
    const items = data?.items;
    if (!items || items.length === 0) throw new Error("No items in feed");

    const posts: Post[] = [];
    const limit = Math.min(items.length, count);
    for (let i = 0; i < limit; i++) {
      const item = items[i];
      const img = extractThumbnail(item, feed.fallback.img);
      posts.push({
        title: decodeHtmlEntities(item.title || feed.fallback.title),
        tag: feed.tag,
        date: formatDate(item.pubDate || item.published || ""),
        img,
        href: item.link || feed.fallback.href,
      });
    }
    return posts;
  } catch {
    return [feed.fallback];
  }
}

function getFallbackImg(tag: string): string {
  const feed = FEEDS.find(f => f.tag === tag);
  return feed?.fallback.img || '';
}

function padToThree(posts: Post[], fallback: Post): Post[] {
  const result = [...posts];
  while (result.length < 3) {
    result.push({ ...fallback });
  }
  return result.slice(0, 3);
}

export default function Blog() {
  const ref = useReveal<HTMLDivElement>();
  const col1Ref = useReveal<HTMLDivElement>();
  const col2Ref = useReveal<HTMLDivElement>();
  const col3Ref = useReveal<HTMLDivElement>();
  const [tistoryPosts, setTistoryPosts] = useState<Post[]>(
    Array(3).fill(null).map(() => ({ ...FALLBACK_TISTORY }))
  );
  const [bloggerPosts, setBloggerPosts] = useState<Post[]>(
    Array(3).fill(null).map(() => ({ ...FALLBACK_BLOGGER }))
  );
  const [naverPosts, setNaverPosts] = useState<Post[]>(
    Array(3).fill(null).map(() => ({ ...FALLBACK_NAVER }))
  );
  const [lastUpdated, setLastUpdated] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled(
      FEEDS.map((feed) => fetchPostsFromFeed(feed, 3))
    ).then((results) => {
      if (cancelled) return;

      if (results[0].status === "fulfilled") {
        setTistoryPosts(padToThree(results[0].value, FALLBACK_TISTORY));
      }
      if (results[1].status === "fulfilled") {
        setBloggerPosts(padToThree(results[1].value, FALLBACK_BLOGGER));
      }
      if (results[2].status === "fulfilled") {
        setNaverPosts(padToThree(results[2].value, FALLBACK_NAVER));
      }

      setLastUpdated(formatTime(new Date().toISOString()));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="blog" className="relative w-full py-24 md:py-32 bg-white overflow-hidden">
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-point-50/25 via-white to-neutral-50/50" />
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-point-50/15 via-transparent to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-point-100/10 via-transparent to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="w-full px-6 md:px-12 lg:px-16 relative z-10">
        <div ref={ref} className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-point-500"></span>
              <span className="text-point-600 text-xs font-semibold uppercase tracking-[0.3em]">
                Field Insights
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/60">
                <span className="relative flex w-2 h-2">
                  <span className="live-dot absolute inline-flex w-full h-full rounded-full bg-emerald-500"></span>
                </span>
                <span className="text-[11px] font-semibold text-emerald-700 whitespace-nowrap">실시간 연동</span>
              </span>
            </div>
            <h2 className="font-hero text-navy-900 text-3xl md:text-5xl lg:text-6xl leading-tight mb-6">
              현장에서 나온 기술자료
            </h2>
            <p className="text-neutral-600 text-base md:text-lg leading-relaxed">
              시공 현장에서 축적한 노하우와 자재별 성능 데이터를 티스토리 · 구글블로그 · 네이버에
              공유합니다.
            </p>
            {lastUpdated && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-neutral-400 text-xs">
                <i className="ri-time-line text-[11px]"></i>
                마지막 업데이트: {lastUpdated}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 self-start">
            <a
              href="https://jinc1.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="티스토리 블로그"
              className="group w-11 h-11 flex items-center justify-center rounded-full bg-[#FF5A4A] hover:bg-[#FF5A4A]/85 text-white text-lg font-bold transition-all cursor-pointer"
            >
              <span className="text-base leading-none">T</span>
            </a>
            <a
              href="https://blog.jinc1.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="구글 블로그"
              className="group w-11 h-11 flex items-center justify-center rounded-full bg-[#F57C00] hover:bg-[#F57C00]/85 text-white text-lg transition-all cursor-pointer"
            >
              <i className="fa-brands fa-blogger-b text-lg"></i>
            </a>
            <a
              href="https://blog.naver.com/ksh1009000"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="네이버 블로그"
              className="group w-11 h-11 flex items-center justify-center rounded-full bg-[#03C75A] hover:bg-[#03C75A]/85 text-white text-lg font-bold transition-all cursor-pointer"
            >
              <span className="text-base leading-none">N</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 기술자료 · 티스토리 */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 pb-2 border-b border-neutral-100">
              <a
                href="https://jinc1.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="티스토리 블로그"
                className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FF5A4A] hover:bg-[#FF5A4A]/85 text-white text-xs font-bold transition-all hover:scale-110 cursor-pointer"
              >
                <span className="leading-none">T</span>
              </a>
              <a
                href="https://jinc1.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-sm font-semibold text-navy-900 hover:text-[#FF5A4A] transition-colors cursor-pointer whitespace-nowrap"
              >
                기술자료
              </a>
              <span className="text-xs text-neutral-300 select-none">·</span>
              <a
                href="https://jinc1.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-medium text-[#FF5A4A] hover:underline transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
                티스토리
              </a>
            </div>
            <div ref={col1Ref} className="reveal reveal-stagger flex flex-col gap-4">
              {tistoryPosts.map((p, idx) => (
                <a
                  key={`tistory-${idx}`}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="reveal-item group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 hover:border-point-400/60 ring-1 ring-transparent hover:ring-point-400/30 bg-white transition-all duration-500 ease-out hover:-translate-y-3 cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        const fallback = getFallbackImg(p.tag);
                        if (target.src !== fallback) {
                          target.src = fallback;
                        } else {
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          const fb = parent?.querySelector('.blog-img-fallback') as HTMLElement;
                          if (fb) {
                            fb.classList.remove('hidden');
                            fb.classList.add('flex');
                          }
                        }
                      }}
                    />
                    <div className="absolute inset-0 flex-col items-center justify-center gap-2 hidden blog-img-fallback bg-gradient-to-br from-[#FF5A4A]/10 to-[#FF5A4A]/5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/80">
                        <span className="text-xl font-bold text-[#FF5A4A]/60">T</span>
                      </div>
                      <span className="text-xs text-neutral-400 font-medium">이미지 준비중</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out"></div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
                      <span>{p.date}</span>
                      <span>·</span>
                      <span>제이아이앤씨</span>
                    </div>
                    <h4 className="font-hero text-navy-900 text-base leading-snug mb-4 group-hover:text-point-600 transition-colors line-clamp-2">
                      <span className="cursor-pointer">{p.title}</span>
                    </h4>
                    <div className="mt-auto flex items-center gap-2 text-sm text-navy-900 font-semibold">
                      Read Article
                      <div className="w-5 h-5 flex items-center justify-center transition-transform group-hover:translate-x-2">
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <a
              href="https://jinc1.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="reveal-item group flex items-center justify-center gap-2 mt-1 px-5 py-3 rounded-xl border border-[#FF5A4A]/20 bg-[#FF5A4A]/5 hover:bg-[#FF5A4A]/10 text-sm font-semibold text-[#FF5A4A] transition-all cursor-pointer"
            >
              더 많은 포스트 보기
              <div className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>

          {/* 심층자료 · 구글블로그 */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 pb-2 border-b border-neutral-100">
              <a
                href="https://blog.jinc1.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="구글 블로그"
                className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F57C00] hover:bg-[#F57C00]/85 text-white transition-all hover:scale-110 cursor-pointer"
              >
                <i className="fa-brands fa-blogger-b text-xs"></i>
              </a>
              <a
                href="https://blog.jinc1.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-sm font-semibold text-navy-900 hover:text-[#F57C00] transition-colors cursor-pointer whitespace-nowrap"
              >
                심층자료
              </a>
              <span className="text-xs text-neutral-300 select-none">·</span>
              <a
                href="https://blog.jinc1.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-medium text-[#F57C00] hover:underline transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
                구글블로그
              </a>
            </div>
            <div ref={col2Ref} className="reveal reveal-stagger flex flex-col gap-4">
              {bloggerPosts.map((p, idx) => (
                <a
                  key={`blogger-${idx}`}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="reveal-item group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 hover:border-point-400/60 ring-1 ring-transparent hover:ring-point-400/30 bg-white transition-all duration-500 ease-out hover:-translate-y-3 cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        const fallback = getFallbackImg(p.tag);
                        if (target.src !== fallback) {
                          target.src = fallback;
                        } else {
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          const fb = parent?.querySelector('.blog-img-fallback') as HTMLElement;
                          if (fb) {
                            fb.classList.remove('hidden');
                            fb.classList.add('flex');
                          }
                        }
                      }}
                    />
                    <div className="absolute inset-0 flex-col items-center justify-center gap-2 hidden blog-img-fallback bg-gradient-to-br from-[#F57C00]/10 to-[#F57C00]/5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/80">
                        <i className="fa-brands fa-blogger-b text-xl text-[#F57C00]/60"></i>
                      </div>
                      <span className="text-xs text-neutral-400 font-medium">이미지 준비중</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out"></div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
                      <span>{p.date}</span>
                      <span>·</span>
                      <span>제이아이앤씨</span>
                    </div>
                    <h4 className="font-hero text-navy-900 text-base leading-snug mb-4 group-hover:text-point-600 transition-colors line-clamp-2">
                      <span className="cursor-pointer">{p.title}</span>
                    </h4>
                    <div className="mt-auto flex items-center gap-2 text-sm text-navy-900 font-semibold">
                      Read Article
                      <div className="w-5 h-5 flex items-center justify-center transition-transform group-hover:translate-x-2">
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <a
              href="https://blog.jinc1.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="reveal-item group flex items-center justify-center gap-2 mt-1 px-5 py-3 rounded-xl border border-[#F57C00]/20 bg-[#F57C00]/5 hover:bg-[#F57C00]/10 text-sm font-semibold text-[#F57C00] transition-all cursor-pointer"
            >
              더 많은 포스트 보기
              <div className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>

          {/* 시공사례 · 네이버 */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 pb-2 border-b border-neutral-100">
              <a
                href="https://blog.naver.com/ksh1009000"
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="네이버 블로그"
                className="w-6 h-6 flex items-center justify-center rounded-full bg-[#03C75A] hover:bg-[#03C75A]/85 text-white text-xs font-bold transition-all hover:scale-110 cursor-pointer"
              >
                <span className="leading-none">N</span>
              </a>
              <a
                href="https://blog.naver.com/ksh1009000"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-sm font-semibold text-navy-900 hover:text-[#03C75A] transition-colors cursor-pointer whitespace-nowrap"
              >
                시공사례
              </a>
              <span className="text-xs text-neutral-300 select-none">·</span>
              <a
                href="https://blog.naver.com/ksh1009000"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-medium text-[#03C75A] hover:underline transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
                네이버
              </a>
            </div>
            <div ref={col3Ref} className="reveal reveal-stagger flex flex-col gap-4">
              {naverPosts.map((p, idx) => (
                <a
                  key={`naver-${idx}`}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="reveal-item group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 hover:border-point-400/60 ring-1 ring-transparent hover:ring-point-400/30 bg-white transition-all duration-500 ease-out hover:-translate-y-3 cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        const fallback = getFallbackImg(p.tag);
                        if (target.src !== fallback) {
                          target.src = fallback;
                        } else {
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          const fb = parent?.querySelector('.blog-img-fallback') as HTMLElement;
                          if (fb) {
                            fb.classList.remove('hidden');
                            fb.classList.add('flex');
                          }
                        }
                      }}
                    />
                      <div className="absolute inset-0 flex-col items-center justify-center gap-2 hidden blog-img-fallback bg-gradient-to-br from-[#03C75A]/10 to-[#03C75A]/5">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/80">
                          <span className="text-xl font-bold text-[#03C75A]/60">N</span>
                        </div>
                        <span className="text-xs text-neutral-400 font-medium">이미지 준비중</span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out"></div>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
                        <span>{p.date}</span>
                        <span>·</span>
                        <span>제이아이앤씨</span>
                      </div>
                      <h4 className="font-hero text-navy-900 text-base leading-snug mb-4 group-hover:text-point-600 transition-colors line-clamp-2">
                        <span className="cursor-pointer">{p.title}</span>
                      </h4>
                      <div className="mt-auto flex items-center gap-2 text-sm text-navy-900 font-semibold">
                        Read Article
                        <div className="w-5 h-5 flex items-center justify-center transition-transform group-hover:translate-x-2">
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </div>
                    </div>
                  </a>
              ))}
              </div>
            <a
              href="https://blog.naver.com/ksh1009000"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="reveal-item group flex items-center justify-center gap-2 mt-1 px-5 py-3 rounded-xl border border-[#03C75A]/20 bg-[#03C75A]/5 hover:bg-[#03C75A]/10 text-sm font-semibold text-[#03C75A] transition-all cursor-pointer"
            >
              더 많은 포스트 보기
              <div className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}